import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  afterNextRender,
  computed,
  effect,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
  BUSCA_MIN_CARACTERES,
  BuscaGlobalService,
  GrupoBusca,
  ItemBusca,
  ResultadoBusca,
} from '../../services/busca-global.service';

/** Atalho de navegação (item do menu já filtrado por papel/flag pelo chamador). */
export interface AtalhoBusca {
  rota: string;
  rotulo: string;
  icone: string;
}

interface OpcaoBusca extends ItemBusca {
  id: string;
  icone: string;
}

interface SecaoBusca {
  id: string;
  rotulo: string;
  opcoes: OpcaoBusca[];
  erro: boolean;
}

interface SecaoBruta {
  id: string;
  rotulo: string;
  erro: boolean;
  itens: Omit<OpcaoBusca, 'id'>[];
}

const FOCAVEIS = 'button:not([disabled]), input:not([disabled]), [href]';

const normalizar = (t: string): string =>
  t
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase();

/** Paleta de busca global (Ctrl/Cmd+K), padrão ARIA combobox + listbox. */
@Component({
  selector: 'app-busca-global',
  templateUrl: './busca-global.component.html',
  styleUrls: ['./busca-global.component.css'],
})
export class BuscaGlobalComponent {
  readonly atalhos = input<AtalhoBusca[]>([]);

  private readonly servico = inject(BuscaGlobalService);
  private readonly router = inject(Router);
  private readonly injector = inject(Injector);

  readonly aberta = this.servico.aberta;
  readonly termo = signal('');
  readonly carregando = signal(false);
  readonly resultado = signal<ResultadoBusca | null>(null);
  readonly indice = signal(0);
  readonly minimo = BUSCA_MIN_CARACTERES;

  private readonly termo$ = new Subject<string>();
  private readonly dialogo = viewChild<ElementRef<HTMLElement>>('dialogo');
  private readonly campo = viewChild<ElementRef<HTMLInputElement>>('campo');

  readonly termoValido = computed(() => this.termo().trim().length >= BUSCA_MIN_CARACTERES);

  readonly secoes = computed<SecaoBusca[]>(() => {
    const secoes: SecaoBruta[] = [];
    const r = this.resultado();
    const grupo = (id: string, rotulo: string, g: GrupoBusca | null, icone: string): void => {
      if (g) {
        secoes.push({
          id,
          rotulo,
          erro: g.erro,
          itens: g.itens.map((i) => ({ ...i, icone })),
        });
      }
    };
    if (this.termoValido() && r) {
      grupo('clientes', 'Clientes', r.clientes, 'bi-people');
      grupo('tenants', 'Tenants', r.tenants, 'bi-diagram-3');
    }
    const q = normalizar(this.termo().trim());
    const atalhos = this.atalhos()
      .filter((a) => !q || normalizar(a.rotulo).includes(q))
      .map((a) => ({ rotulo: a.rotulo, detalhe: null, rota: a.rota, icone: a.icone }));
    secoes.push({ id: 'atalhos', rotulo: 'Ir para', erro: false, itens: atalhos });

    let n = 0;
    return secoes
      .map((s) => ({
        id: s.id,
        rotulo: s.rotulo,
        erro: s.erro,
        opcoes: s.itens.map((i) => ({ ...i, id: `busca-opcao-${n++}` })),
      }))
      .filter((s) => s.opcoes.length > 0 || s.erro);
  });

  readonly opcoes = computed(() => this.secoes().flatMap((s) => s.opcoes));
  readonly ativa = computed<OpcaoBusca | null>(() => this.opcoes()[this.indice()] ?? null);
  readonly semResultados = computed(
    () => !this.carregando() && this.opcoes().length === 0 && !this.secoes().some((s) => s.erro),
  );

  constructor() {
    this.servico
      .consultar(this.termo$)
      .pipe(takeUntilDestroyed())
      .subscribe((r) => {
        this.resultado.set(r);
        this.carregando.set(false);
        this.indice.set(0);
      });

    let estavaAberta = false;
    effect(() => {
      const aberta = this.aberta();
      if (aberta && !estavaAberta) {
        this.termo.set('');
        this.resultado.set(null);
        this.carregando.set(false);
        this.indice.set(0);
        afterNextRender(() => this.campo()?.nativeElement.focus(), { injector: this.injector });
      }
      estavaAberta = aberta;
    });
  }

  @HostListener('document:keydown', ['$event'])
  aoTeclarGlobal(ev: KeyboardEvent): void {
    if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 'k') {
      ev.preventDefault();
      this.servico.alternar();
      return;
    }
    if (ev.key === '/' && !ev.ctrlKey && !ev.metaKey && !ev.altKey && !this.aberta() && !this.emCampoDeTexto(ev)) {
      ev.preventDefault();
      this.servico.abrir();
    }
  }

  aoDigitar(valor: string): void {
    this.termo.set(valor);
    this.indice.set(0);
    const valido = valor.trim().length >= BUSCA_MIN_CARACTERES;
    this.carregando.set(valido);
    if (!valido) {
      this.resultado.set(null);
    }
    this.termo$.next(valor);
  }

  aoTeclar(ev: KeyboardEvent): void {
    const total = this.opcoes().length;
    switch (ev.key) {
      case 'Escape':
        ev.preventDefault();
        this.servico.fechar();
        return;
      case 'ArrowDown':
        ev.preventDefault();
        if (total) {
          this.indice.set((this.indice() + 1) % total);
          this.rolarParaAtiva();
        }
        return;
      case 'ArrowUp':
        ev.preventDefault();
        if (total) {
          this.indice.set((this.indice() - 1 + total) % total);
          this.rolarParaAtiva();
        }
        return;
      case 'Enter': {
        const op = this.ativa();
        if (op) {
          ev.preventDefault();
          this.ir(op);
        }
        return;
      }
      case 'Tab':
        this.prenderFoco(ev);
        return;
    }
  }

  ir(op: OpcaoBusca): void {
    this.servico.fechar(false);
    void this.router.navigate([op.rota]);
  }

  fechar(): void {
    this.servico.fechar();
  }

  private prenderFoco(ev: KeyboardEvent): void {
    const raiz = this.dialogo()?.nativeElement;
    const itens = raiz ? Array.from(raiz.querySelectorAll<HTMLElement>(FOCAVEIS)) : [];
    if (itens.length === 0) {
      ev.preventDefault();
      return;
    }
    const primeiro = itens[0];
    const ultimo = itens[itens.length - 1];
    const ativo = document.activeElement;
    if (ev.shiftKey && ativo === primeiro) {
      ev.preventDefault();
      ultimo.focus();
    } else if (!ev.shiftKey && ativo === ultimo) {
      ev.preventDefault();
      primeiro.focus();
    }
  }

  private rolarParaAtiva(): void {
    const id = this.ativa()?.id;
    const el = id ? this.dialogo()?.nativeElement.querySelector<HTMLElement>(`#${id}`) : null;
    el?.scrollIntoView?.({ block: 'nearest' });
  }

  private emCampoDeTexto(ev: KeyboardEvent): boolean {
    const alvo = ev.target;
    if (!(alvo instanceof HTMLElement)) {
      return false;
    }
    return alvo.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(alvo.tagName);
  }
}
